import * as React from 'react'
import { faCheck } from '@fortawesome/pro-light-svg-icons'

import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

import { DateTime } from '@espoon-voltti/ts-libs/lib/time'

import { Section } from '@espoon-voltti/react-components/layout'
import {
  Title,
  Columns,
  Column,
  Icon
} from '@espoon-voltti/react-components/elements'

import { Representative } from '@espoon-voltti/nuora-domain/applicants'

import { SigningData } from '../../../domain/grants'

import { t } from '../../../i18n'

interface Props {
  representatives: Representative[]
  signingData: SigningData | null
}

export const Signers: React.FC<Props> = (p) => {
  return (
    <Section>
      <Title size={4}>{t(['views', 'signing', 'org', 'signers'])}</Title>
      {p.representatives.map((r) =>
        pipe(
          O.fromNullable(p.signingData),
          O.chain((signing) =>
            pipe(
              O.fromNullable(
                signing.signers.find((x) => x.ssnHash === r.ssnHash)
              ),
              O.map((x) => (
                <SignedRow key={r.ssnHash} signer={r} signedAt={x.signedAt} />
              ))
            )
          ),
          O.getOrElse(() => <UnsignedRow key={r.ssnHash} signer={r} />)
        )
      )}
    </Section>
  )
}

const SignedRow: React.FC<{ signer: Representative; signedAt: DateTime }> = ({
  signer,
  signedAt
}) => (
  <Columns dataQa={'signer'}>
    <Column dataQa={'roleAndName'}>
      <strong>{getSignerNameLabel(signer)}</strong>
    </Column>
    <Column size={4} dataQa={'signedAt'}>
      <Icon status={'success'} icon={faCheck} size={'small'} round={true} />
      <strong>
        {' '}
        {t(['views', 'preview', 'signing', 'signedAt'], {
          date: signedAt.format()
        })}
      </strong>
    </Column>
  </Columns>
)

const UnsignedRow: React.FC<{ signer: Representative }> = ({ signer }) => (
  <Columns dataQa={'signer'}>
    <Column dataQa={'roleAndName'}>{getSignerNameLabel(signer)}</Column>
    <Column size={4} />
  </Columns>
)

const getSignerNameLabel = (x: Representative) =>
  x.role
    ? `${t(['org', 'representativeRole', x.role])}: ${x.firstName} ${
        x.lastName
      }, ${x.email}`
    : `${x.firstName} ${x.lastName}, ${x.email}`
